import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useQuizListByBranchId } from '@/api/queries/direcionada/administration/quiz-queries'
import { usePeople } from '@/api/queries/people-queries'
import { DetailField, InspectionDetailsSection } from '@/components/commons'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	DirectedFreeTextQuestionCard,
	DirectedQuestionCard,
	InspectionDateField,
	InspectionDetailsHeader,
} from '@/components/inspections'
import { Button } from '@/components/ui/button'
import { FieldContent } from '@/components/ui/field'
import { Separator } from '@/components/ui/separator'
import { useDirecionadaForm } from '@/hooks/forms/use-direcionada-form'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import type {
	DirecionadaFormData,
	DirecionadaInput,
} from '@/schemas/direcionada-schema'
import { useProfileStore } from '@/store'
import type {
	AnswersInput,
	DirecionadaOutput,
	DirecionadaUpdateInput,
} from '@/types/direcionada'
import { capitalize } from '@/utils/capitalize'
import {
	formatDateToDisplay,
	formatDateYMD,
	parseStringToDateForDisplay,
} from '@/utils/formatDate'
import { Spinner } from '../ui/spinner'

const ANSWER_VALUES = ['COMPLIANT', 'NON_COMPLIANT', 'NOT_APPLICABLE'] as const
const STATUS_VALUES = ['IN_PROGRESS', 'RESOLVED'] as const

const toAnswerValue = (value: unknown) =>
	ANSWER_VALUES.includes(value as (typeof ANSWER_VALUES)[number])
		? (value as DirecionadaInput['answers'][number]['answer'])
		: undefined

const toStatusValue = (value: unknown) =>
	STATUS_VALUES.includes(value as (typeof STATUS_VALUES)[number])
		? (value as DirecionadaInput['answers'][number]['status'])
		: undefined

export interface CorrectDirectedFormProps {
	directedSelected?: DirecionadaOutput
	isLoading?: boolean
	isDisabled?: boolean
	statusOnly?: boolean
	onSave: (data: DirecionadaUpdateInput) => void
	onClose: () => void
}

export function CorrectDirectedFormContent({
	directedSelected,
	isLoading = false,
	isDisabled = false,
	statusOnly = false,
	onSave,
	onClose,
}: CorrectDirectedFormProps) {
	const { t } = useTranslation()
	const form = useDirecionadaForm()
	const {
		setFocus,
		formState: { errors },
	} = form
	const { handleError } = useFormErrorFocus(errors, setFocus)

	const { defaultBranchId } = useProfileStore()
	const { data: quizList } = useQuizListByBranchId(defaultBranchId)
	const { data: employeeData } = usePeople()

	const QUESTIONS_SELECT = useMemo(
		() =>
			quizList?.map((quiz) => ({
				id: quiz.id,
				name: quiz.title,
				kind: quiz.kind,
				questions: quiz.questions.map((q) => ({
					id: q.id,
					title: q.question,
					imagePath: q.imagePath,
				})),
			})) || [],
		[quizList],
	)

	useEffect(() => {
		if (!directedSelected) return

		const normalizedDate = parseStringToDateForDisplay(
			directedSelected.date ? String(directedSelected.date) : '',
		)

		const currentQuiz = QUESTIONS_SELECT.find(
			(quiz) => quiz.id === directedSelected.quizId,
		)

		const answersByQuestionId = new Map<
			string,
			DirecionadaInput['answers'][number]
		>(
			(directedSelected.answers ?? []).map((answer) => {
				const dueDate = parseStringToDateForDisplay(
					answer.dueDate ? String(answer.dueDate) : '',
				)
				const normalized: DirecionadaInput['answers'][number] = {
					quizQuestionId: answer.quizQuestionId,
					answer: toAnswerValue(answer.answer),
					textAnswer: answer.textAnswer ?? undefined,
					responsibleId: answer.responsibleId ?? undefined,
					actionPlan: answer.actionPlan || undefined,
					status: toStatusValue(answer.status),
					dueDate: dueDate ? formatDateYMD(dueDate) : undefined,
					imagePath: answer.imagePath ?? undefined,
					removeImage: false,
				}
				return [answer.quizQuestionId, normalized] as const
			}),
		)

		const answers: DirecionadaInput['answers'] = currentQuiz
			? currentQuiz.questions.reduce<DirecionadaInput['answers']>(
					(acc, question) => {
						const existing = answersByQuestionId.get(question.id)
						if (existing) acc.push(existing)
						return acc
					},
					[],
				)
			: Array.from(answersByQuestionId.values())

		form.reset({
			date: normalizedDate ? formatDateYMD(normalizedDate) : '',
			quizId: directedSelected.quizId ?? '',
			answers,
		})
	}, [QUESTIONS_SELECT, directedSelected, form])

	const handleSave = (formData: DirecionadaFormData) => {
		if (!formData.date || !formData.quizId) {
			toast.error(t('inspections.directed.validationError'))
			return
		}

		const normalizedFormDate = parseStringToDateForDisplay(formData.date)

		const currentQuiz = QUESTIONS_SELECT.find((q) => q.id === formData.quizId)
		const validQuestionIds = new Set(
			(currentQuiz?.questions ?? []).map((question) => question.id),
		)
		const scopedAnswers = formData.answers.filter((answer) =>
			validQuestionIds.has(answer.quizQuestionId),
		)

		const isFreeText = currentQuiz?.kind === 'FREE_TEXT'

		if (
			!scopedAnswers.length ||
			scopedAnswers.some((answer) =>
				isFreeText ? !answer.textAnswer?.trim() : !answer.answer,
			)
		) {
			toast.error(t('inspections.directed.validationError'))
			return
		}

		const answersPayload: AnswersInput[] = scopedAnswers.map((answer) => {
			const entry: AnswersInput = isFreeText
				? {
						quizQuestionId: answer.quizQuestionId,
						textAnswer: answer.textAnswer?.trim(),
					}
				: {
						quizQuestionId: answer.quizQuestionId,
						answer: answer.answer!,
					}

			if (answer.image instanceof File) {
				entry.image = answer.image
			}

			if (answer.removeImage) {
				entry.removeImage = true
			}

			if (answer.answer === 'NON_COMPLIANT') {
				if (answer.responsibleId) entry.responsibleId = answer.responsibleId
				if (answer.status) entry.status = answer.status
				if (answer.actionPlan) entry.actionPlan = answer.actionPlan
				if (answer.dueDate) {
					const normalizedDueDate = parseStringToDateForDisplay(answer.dueDate)
					if (normalizedDueDate) {
						entry.dueDate = formatDateYMD(normalizedDueDate)
					}
				}
			}

			return entry
		})

		// Envia apenas os campos que o usuario alterou (diff contra o form.reset inicial).
		// As respostas vao sempre completas quando qualquer uma delas mudou, pois o
		// backend recebe a lista inteira.
		const { dirtyFields } = form.formState
		const hasDirtyAnswer = Array.isArray(dirtyFields.answers)
			? dirtyFields.answers.some(
					(flags) => flags && Object.values(flags).some(Boolean),
				)
			: Boolean(dirtyFields.answers)
		const hasAnswerImageChange = scopedAnswers.some(
			(answer) => answer.image instanceof File || answer.removeImage,
		)

		const payload: DirecionadaUpdateInput = {}

		if (dirtyFields.quizId) {
			payload.quizId = formData.quizId
		}
		if (dirtyFields.date && normalizedFormDate) {
			payload.date = formatDateYMD(normalizedFormDate)
		}
		if (dirtyFields.quizId || hasDirtyAnswer || hasAnswerImageChange) {
			payload.answers = answersPayload
		}

		if (Object.keys(payload).length === 0) {
			onClose()
			return
		}

		onSave(payload)
	}

	const activeQuestionnaire = QUESTIONS_SELECT.find(
		(q) => q.id === form.watch('quizId'),
	)
	const watchedAnswers = form.watch('answers')
	const displayedAnswers = useMemo(
		() =>
			watchedAnswers
				.map((answer, idx) => ({ answer, idx }))
				.filter(({ answer }) =>
					statusOnly ? answer.answer === 'NON_COMPLIANT' : true,
				),
		[statusOnly, watchedAnswers],
	)

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSave, handleError)}
				className="flex flex-col gap-5 px-1"
			>
				{statusOnly && directedSelected && (
					<>
						<div className="space-y-5 py-1">
							<InspectionDetailsHeader
								title={
									<DetailField
										label={t('inspections.common.questionnaire')}
										value={directedSelected.quizTitle}
									/>
								}
								date={formatDateToDisplay(directedSelected.date)}
								createdBy={capitalize(directedSelected.createdByName)}
							/>

							{directedSelected.customer?.id && (
								<InspectionDetailsSection>
									<DetailField
										label={t('inspections.common.customer')}
										value={capitalize(directedSelected.customer.name)}
									/>
								</InspectionDetailsSection>
							)}
						</div>

						<Separator className="bg-border/60" />
					</>
				)}

				{!statusOnly && (
					<>
						<FormField
							name="date"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.common.date')}
									error={form.formState.errors.date?.message}
								>
									<InspectionDateField
										value={
											field.value
												? parseStringToDateForDisplay(field.value)
												: undefined
										}
										onChange={(date) =>
											field.onChange(date ? formatDateYMD(date) : '')
										}
									/>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />
					</>
				)}

				{activeQuestionnaire && (
					<>
						<Separator className="bg-border/60" />
						<div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
							<div className="flex items-center justify-between">
								<h2 className="text-base font-semibold text-kaefer-dark-grey">
									{statusOnly
										? t('inspections.directed.status')
										: activeQuestionnaire.name}
								</h2>
								<span className="text-xs text-muted-foreground tabular-nums">
									{displayedAnswers.length}{' '}
									{t('inspections.directed.answersTitle').toLowerCase()}
								</span>
							</div>

							<FormField
								name="answers"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label=""
										error={form.formState.errors.answers?.message}
									>
										<FieldContent fieldName={field.name}>
											<div className="space-y-3">
												{employeeData &&
													displayedAnswers.map(({ answer, idx }) => {
														const question = activeQuestionnaire.questions.find(
															(q) => q.id === answer.quizQuestionId,
														)
														if (!question) return null
														if (activeQuestionnaire.kind === 'FREE_TEXT') {
															return (
																<DirectedFreeTextQuestionCard
																	key={question.id}
																	question={question}
																	index={idx + 1}
																	answerIndex={idx}
																/>
															)
														}
														return (
															<DirectedQuestionCard
																key={question.id}
																question={question}
																index={idx + 1}
																answerIndex={idx}
																responsibles={employeeData}
																statusOnly={statusOnly}
															/>
														)
													})}
											</div>
										</FieldContent>
									</FormItem>
								)}
							/>
						</div>
					</>
				)}

				<div className="flex gap-3 pt-1">
					{isLoading ? (
						<Button disabled={isLoading} type="button" className="flex-1">
							<Spinner /> {t('inspections.directed.saving')}
						</Button>
					) : (
						<>
							<Button
								type="button"
								variant="cancel"
								onClick={onClose}
								className="flex-1"
							>
								{t('common.cancel')}
							</Button>
							<Button
								type="submit"
								disabled={isDisabled}
								className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t('inspections.directed.save')}
							</Button>
						</>
					)}
				</div>
			</form>
		</Form>
	)
}
